<template>
  <div v-if="isBig" class="base-conference-button isBig">
    <a
      ref="buttonEl"
      class="link"
      @click="openUrl()"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
    >
      <MeetingIcon :meeting-icon="meetingIcon" />
    </a>
  </div>
  <div v-else class="base-conference-button" :class="{ isRounded }">
    <BaseButton
      kind="auxiliary"
      true-colors-icon
      :tooltip="tooltipContent"
      :tooltip-display="
        hasTooltip.withShortcut || hasTooltip.withText ? 'always' : 'none'
      "
      :text="isMeetingNameShown ? `Join ${meetingName}` : ''"
      @click.stop="openUrl"
    >
      <template #icon>
        <GMeetIcon v-if="meetingName === 'Google Meet'" />
        <MeetingIcon v-else :meeting-icon="meetingIcon" :width="13" />
      </template>
    </BaseButton>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
} from "vue";

import { eventActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import GMeetIcon from "@/components/icons/apps/google/GMeetIcon.vue";
import MeetingIcon from "@/components/icons/MeetingIcon.vue";

import { TooltipContent, useTooltip } from "../tooltip";

const hasTooltipDefaultValue = { withText: true, withShortcut: true };

export default defineComponent({
  components: {
    BaseButton,
    GMeetIcon,
    MeetingIcon,
  },
  props: {
    hasTooltip: {
      type: Object as PropType<{ withText: boolean; withShortcut: boolean }>,
      default: hasTooltipDefaultValue,
    },
    isBig: { type: Boolean, default: false },
    isMeetingNameShown: { type: Boolean, default: false },
    isRounded: { type: Boolean, default: true },
    meetingIcon: { type: String, default: "meeting" },
    meetingName: { type: String, default: "meeting" },
  },
  emits: {
    openUrl: () => true,
  },
  setup(props, { emit }) {
    const { hasTooltip, isBig, meetingName } = toRefs(props);
    const buttonEl = ref<HTMLElement>();
    const openUrl = () => emit("openUrl");
    const tooltipContent = computed<TooltipContent>(() =>
      hasTooltip.value
        ? {
            text: hasTooltip.value.withText
              ? `Join ${meetingName.value}`
              : undefined,
            shortcut: hasTooltip.value.withShortcut
              ? eventActions.openConference.shortcutsDisplay
              : undefined,
          }
        : undefined
    );
    const { showTooltip, hideTooltip, release } = useTooltip(
      buttonEl,
      tooltipContent,
      "left",
      isBig
    );
    onBeforeUnmount(release);
    return {
      buttonEl,
      hideTooltip,
      openUrl,
      showTooltip,
      tooltipContent,
    };
  },
});
</script>
<style scoped lang="scss">
.base-conference-button {
  &.isBig {
    a {
      display: inline-block;
      padding: 32px;
      border-radius: 100px;
      animation: blink 1s linear infinite;
      @include transition(background-color 100ms ease);
      @include themed() {
        background: t($bg-menu-hover);
      }

      &:hover {
        @include themed() {
          background: t($grey-15);
        }
      }

      &:active {
        @include themed() {
          background: t($grey-20);
        }
      }

      img {
        width: 40px;
        vertical-align: -20%;
      }
    }
  }
  &.isRounded {
    button.base-button {
      border-radius: 43px;
    }
  }

  button.base-button {
    :deep(.icon),
    :deep(.icon svg) {
      height: 12px;
      width: 12px;
    }
  }
}
</style>
